import type { TableColumnType, TableRowType } from 'components/editor/grid/reduxStore/table.types';
import { ColumnTypeIdentifier, RowTypesIdentifier } from 'components/editor/grid/reduxStore/table.types';
import { buildCrmChildRowId } from 'components/editor/grid/reduxStore/crmRowIds';
import { isGeneratedRow } from 'components/editor/grid/reduxStore/crmRowMarkers';
import { stringifyNodeValue } from 'components/editor/variables/utils';
import type { CrmArrayLeaf, CrmPasteField } from './crmArrayPayload';
import type { TipTapJsonNode } from './crmChipDocs';
import {
  chipNamesIn,
  instanceChipNode,
  mergeChipIntoStoredValue,
  parseTipTapDoc,
  stampInstanceBinding,
  transformChips,
} from './crmChipDocs';

export type CrmFieldRef = { name: string; path?: string; template?: string };
export type CrmInstanceResolver = (fieldRef: CrmFieldRef) => CrmArrayLeaf[] | undefined;

// Scoped per-column so pasting the same array into two columns doesn't collide; falls back to
// the old bare-template key for documents saved before this scoping shipped.
export const perColumnKey = (columnField: string, templateName: string): string => `${columnField}:${templateName}`;

export const resolveInstancesForRow = (
  row: TableRowType,
  fieldRef: CrmFieldRef,
  resolve: CrmInstanceResolver,
  columnField: string
): CrmArrayLeaf[] | undefined => {
  // Row-persisted instances first: they survive later pastes into other
  // columns, which a resolver built from the current payload cannot.
  const persisted =
    row.crmFieldInstances?.[perColumnKey(columnField, fieldRef.template ?? fieldRef.name)] ??
    row.crmFieldInstances?.[fieldRef.template ?? fieldRef.name];
  if (persisted?.length) return persisted as CrmArrayLeaf[];

  if (fieldRef.template) {
    const byTemplate = resolve(fieldRef);
    if (byTemplate?.length) return byTemplate;
  }

  const groupNames = row.crmFieldGroups?.[perColumnKey(columnField, fieldRef.name)] ?? row.crmFieldGroups?.[fieldRef.name];
  if (groupNames?.length) {
    const resolved = groupNames.map((name) => resolve({ name })?.[0]).filter((leaf): leaf is CrmArrayLeaf => !!leaf);
    return resolved.length > 0 ? resolved : undefined;
  }
  return resolve(fieldRef);
};

const toPersistedInstance = (leaf: CrmArrayLeaf): CrmArrayLeaf => ({
  name: leaf.name,
  friendlyName: leaf.friendlyName,
  value: stringifyNodeValue(leaf.value),
  objectId: leaf.objectId,
  template: leaf.template,
  path: leaf.path,
});

export function buildAuthoredRowPatch(args: {
  row: TableRowType | undefined;
  columns: TableColumnType[];
  field: string;
  fields: CrmPasteField[];
  grouped: boolean;
}): Partial<TableRowType> {
  const { row, columns, field, fields, grouped } = args;
  const [firstField, ...restFields] = fields;
  const patch: Partial<TableRowType> = {};
  if (!firstField) return patch;

  // Resolve which column each field actually lands in — firstField always goes into `field`,
  // restFields spread across the adjacent TEXT columns to the right, if any are available.
  const columnFields = columns.map((column) => column.field);
  const fieldColumnIndex = columnFields.indexOf(field);

  const fieldAssignments: { fieldEntry: CrmPasteField; columnField: string }[] = [{ fieldEntry: firstField, columnField: field }];
  if (fieldColumnIndex !== -1) {
    restFields.forEach((fieldEntry, index) => {
      const columnField = columnFields[fieldColumnIndex + 1 + index];
      if (!columnField) return;
      if (columns[fieldColumnIndex + 1 + index]?.columnType === ColumnTypeIdentifier.TEXT) {
        fieldAssignments.push({ fieldEntry, columnField });
      }
    });
  }

  if (!grouped) {
    patch.crmFieldGroups = {
      ...(row?.crmFieldGroups ?? {}),
      [perColumnKey(field, firstField.template)]: firstField.instances.map((leaf) => leaf.name),
    };
  }

  patch.crmFieldInstances = {
    ...(row?.crmFieldInstances ?? {}),
    ...Object.fromEntries(
      fieldAssignments.map(({ fieldEntry, columnField }) => [
        perColumnKey(columnField, fieldEntry.template),
        fieldEntry.instances.map(toPersistedInstance),
      ])
    ),
  };

  // Insert first field chip into target field
  patch[field] = mergeChipIntoStoredValue(row?.[field], firstField.chipLeaf);

  // Spread remaining fields across adjacent columns
  fieldAssignments.slice(1).forEach(({ fieldEntry, columnField }) => {
    patch[columnField] = mergeChipIntoStoredValue(row?.[columnField], fieldEntry.chipLeaf);
  });

  return patch;
}

function stampRowForInstance(
  row: TableRowType,
  columnFields: string[],
  rowFieldInstances: Map<string, CrmArrayLeaf[]>,
  objectId: string
): Partial<TableRowType> {
  const stampedCells: Partial<TableRowType> = {};

  columnFields.forEach((columnField) => {
    const doc = parseTipTapDoc(row[columnField]);
    if (!doc) return;
    const transformed = transformChips(doc, (_chipName, chipTemplate) => {
      if (!chipTemplate) return undefined;
      const instances = rowFieldInstances.get(chipTemplate);
      if (!instances) return undefined;
      const instance = instances.find((inst) => inst.objectId === objectId);
      return instance ? instanceChipNode(instance) : null;
    });
    if (transformed) stampedCells[columnField] = JSON.stringify(transformed);
  });

  return stampedCells;
}

// Resolves the template row's own chips to one instance's value, keeping each
// chip's own name/path/template so later edits still resolve by field.
function resolveTemplateCells(
  row: TableRowType,
  columnFields: string[],
  rowFieldInstances: Map<string, CrmArrayLeaf[]>,
  objectId: string
): Partial<TableRowType> {
  const resolvedCells: Partial<TableRowType> = {};

  columnFields.forEach((columnField) => {
    const doc = parseTipTapDoc(row[columnField]);
    if (!doc) return;
    const transformed = transformChipNodes(doc, (chipNode) => {
      const chipTemplate = chipNode.attrs?.template ? String(chipNode.attrs.template) : undefined;
      const instance = chipTemplate ? rowFieldInstances.get(chipTemplate)?.find((inst) => inst.objectId === objectId) : undefined;
      return instance ? stampInstanceBinding(chipNode, instance) : chipNode;
    });
    resolvedCells[columnField] = JSON.stringify(transformed);
  });

  return resolvedCells;
}

function transformChipNodes(node: TipTapJsonNode, mapper: (chipNode: TipTapJsonNode) => TipTapJsonNode): TipTapJsonNode {
  if (node.type === 'variable') return mapper(node);
  if (!Array.isArray(node.content)) return node;
  return { ...node, content: node.content.map((child) => transformChipNodes(child, mapper)) };
}

export function deriveCrmRows(
  rows: TableRowType[],
  columns: TableColumnType[],
  resolve: CrmInstanceResolver,
  isTemplate = false
): TableRowType[] {
  // A template has no real linked CRM records — it must keep only the single authored
  // pattern row, never auto-generate child rows that would get baked into every document
  // created from it.
  if (isTemplate) return rows;

  const columnFields = columns.map((column) => column.field);
  const authoredRows = rows.filter((row) => !isGeneratedRow(row));

  const instancesForRow = (row: TableRowType, fieldRef: CrmFieldRef, columnField: string): CrmArrayLeaf[] | undefined =>
    resolveInstancesForRow(row, fieldRef, resolve, columnField);

  const derivedRows: TableRowType[] = [];

  authoredRows.forEach((row) => {
    if (row.rowType !== RowTypesIdentifier.BODY || row.crmRowGroupRole !== undefined) {
      derivedRows.push(row);
      return;
    }

    const rowFieldInstances = new Map<string, CrmArrayLeaf[]>();
    columnFields.forEach((columnField) => {
      chipNamesIn(row[columnField]).forEach((fieldRef) => {
        if (!fieldRef.template || rowFieldInstances.has(fieldRef.template)) return;
        const instances = instancesForRow(row, fieldRef, columnField);
        if (instances) rowFieldInstances.set(fieldRef.template, instances);
      });
    });

    if (rowFieldInstances.size === 0) {
      derivedRows.push(row);
      return;
    }

    // Group instances by objectId to handle canonical structure where instances
    // have distinct identities. Map preserves data order — no sorting.
    const uniqueObjectIds = new Map<string, true>();
    [...rowFieldInstances.values()].forEach((instances) => {
      instances.forEach((instance) => {
        if (instance.objectId) uniqueObjectIds.set(instance.objectId, true);
      });
    });

    if (uniqueObjectIds.size <= 1) {
      derivedRows.push(row);
      return;
    }

    const [firstObjectId, ...restObjectIds] = uniqueObjectIds.keys();

    const firstCells = resolveTemplateCells(row, columnFields, rowFieldInstances, firstObjectId);
    derivedRows.push({ ...row, ...firstCells, crmInstanceObjectId: firstObjectId });

    restObjectIds.forEach((objectId, rowIndex) => {
      const stampedCells = stampRowForInstance(row, columnFields, rowFieldInstances, objectId);

      const { crmFieldInstances: _crmFieldInstances, crmFieldGroups: _crmFieldGroups, ...rowWithoutCrmCache } = row;

      derivedRows.push({
        ...rowWithoutCrmCache,
        ...stampedCells,
        id: buildCrmChildRowId(row.id, rowIndex + 1),
        crmSourceRowId: row.id,
        crmRowKind: 'auto-generated',
        crmInstanceObjectId: objectId,
      });
    });
  });

  return derivedRows;
}
